/**
 * Bilinear interpolation for smooth sampling
 * Used in distortion and rotation effects
 */
export function bilinearSample(
	pixels: Uint8ClampedArray,
	w: number,
	h: number,
	x: number,
	y: number,
): [number, number, number, number] {
	const clampedX = Math.max(0, Math.min(w - 1, x));
	const clampedY = Math.max(0, Math.min(h - 1, y));
	const x0 = Math.floor(clampedX);
	const x1 = Math.min(w - 1, Math.ceil(clampedX));
	const y0 = Math.floor(clampedY);
	const y1 = Math.min(h - 1, Math.ceil(clampedY));
	const dx = clampedX - x0;
	const dy = clampedY - y0;

	function get(ix: number, iy: number) {
		const idx = (iy * w + ix) * 4;
		// Return transparent black for out-of-bounds pixels to avoid artifacts
		if (idx < 0 || idx + 3 >= pixels.length) return [0, 0, 0, 0];
		return [pixels[idx], pixels[idx + 1], pixels[idx + 2], pixels[idx + 3]];
	}

	const c00 = get(x0, y0);
	const c10 = get(x1, y0);
	const c01 = get(x0, y1);
	const c11 = get(x1, y1);
	const c0 = [0, 1, 2, 3].map(
		(i) => (c00[i] ?? 0) * (1 - dx) + (c10[i] ?? 0) * dx,
	);
	const c1 = [0, 1, 2, 3].map(
		(i) => (c01[i] ?? 0) * (1 - dx) + (c11[i] ?? 0) * dx,
	);
	return [0, 1, 2, 3].map(
		(i) => (c0[i] ?? 0) * (1 - dy) + (c1[i] ?? 0) * dy,
	) as [number, number, number, number];
}
